import { PropsWithChildren, useEffect } from 'react';
import { useFeatureToggle } from '../../feature';
import { Features } from '@copilot/data/responses/interface';
import { SmartReplyContext } from './smartReplyContext';
import { SmartReplyPromptPreset } from './smartReplyPromptTypes';
import { IPromptUseCases, usePromptsLazyQuery } from '@copilot/data/graphql/_generated';

const RECOMMENDATION_PROMPT_ID = 'INTERACTIONS_AGENT';

const RECOMMENDATION_PROMPT: SmartReplyPromptPreset = {
    id: RECOMMENDATION_PROMPT_ID,
    label: 'Write a reply for me',
    action: 'WRITE',
    useCases: Object.values(IPromptUseCases),
};

/**
 * Provides the smart reply prompts to the application by fetching the prompts from the backend and storing them in the context
 * @returns
 */
export function SmartReplyPromptPresetsProvider({ children }: PropsWithChildren<{}>): JSX.Element {
    const isSmartReplyEnabled = useFeatureToggle(Features.ChatGPTFeature);
    const [getPrompts, { data }] = usePromptsLazyQuery();
    const availablePrompts: SmartReplyPromptPreset[] = [
        RECOMMENDATION_PROMPT,
        ...(data?.prompts ?? []),
    ];
    const promptPresetLookUp: Record<string, SmartReplyPromptPreset> = availablePrompts.reduce(
        (acc, prompt) => ({ ...acc, [prompt.id]: prompt }),
        {}
    );
    function getByKey(key: string): SmartReplyPromptPreset | undefined {
        return promptPresetLookUp[key];
    }

    useEffect(() => {
        if (isSmartReplyEnabled) getPrompts();
    }, [isSmartReplyEnabled]);

    return (
        <SmartReplyContext.Provider value={{ prompts: availablePrompts, getByKey }}>
            {children}
        </SmartReplyContext.Provider>
    );
}
